import React, { useEffect, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FaChevronRight, FaClipboardCheck, FaUserGraduate, FaTrash, FaLink } from "react-icons/fa";
import Loader from "../../../../components/loader";
import SelectUser from "../../../../components/selectUser";
import api from "../../../../services/api";

const STATUS_LABELS = {
  in_training: { label: "In Training", color: "bg-blue-100 text-blue-700", emoji: "📚" },
  ready_for_evaluation: { label: "Ready for Evaluation", color: "bg-purple-100 text-purple-700", emoji: "✅" },
  graduated: { label: "Graduated", color: "bg-green-100 text-green-700", emoji: "🎓" },
};

const STATUS_TO_PATH = {
  in_training: "/selecole/tracker/in-training",
  ready_for_evaluation: "/selecole/tracker/ready-for-evaluation",
  graduated: "/selecole/tracker/graduated",
};

const getDaysInTraining = (startDate) => {
  if (!startDate) return 0;
  return Math.floor((new Date() - new Date(startDate)) / (1000 * 60 * 60 * 24));
};

export default function Dashboard({ status }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("matrix"); // "matrix" or "table"
  const [sortBy, setSortBy] = useState("name");

  const filterStatus = status || "in_training";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [inTrainingRes, readyRes, graduatedRes] = await Promise.all([
        api.post("/user/search", { limit: 1000, training_status: "in_training" }),
        api.post("/user/search", { limit: 1000, training_status: "ready_for_evaluation" }),
        api.post("/user/search", { limit: 1000, training_status: "graduated" }),
      ]);

      const allTrainingUsers = [
        ...(inTrainingRes.ok ? inTrainingRes.data?.users || [] : []),
        ...(readyRes.ok ? readyRes.data?.users || [] : []),
        ...(graduatedRes.ok ? graduatedRes.data?.users || [] : []),
      ];

      setUsers(allTrainingUsers);
    } catch (error) {
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const addToTraining = async (user) => {
    if (users.find((u) => u._id === user._id)) {
      return toast.error(`${user.name} is already in training`);
    }

    try {
      const { ok, data } = await api.put(`/user/${user._id}`, {
        training_status: "in_training",
        training_phase: "onboarding",
        training_start_date: new Date().toISOString(),
      });
      if (!ok) return toast.error("Failed to add to training");

      setUsers([...users, data]);
      toast.success(`${user.name} added to training! 🚀`);
    } catch (error) {
      toast.error("Error adding to training");
    }
  };

  const markReadyForEvaluation = async (e, userId) => {
    e.stopPropagation();
    try {
      const { ok, data } = await api.put(`/user/${userId}`, {
        training_status: "ready_for_evaluation",
      });
      if (!ok) return toast.error("Failed to update");
      setUsers(users.map((u) => (u._id === userId ? data : u)));
      toast.success("Marked as ready for evaluation");
    } catch (error) {
      toast.error("Error updating");
    }
  };

  const resetTest = async (e, userId, userName) => {
    e.stopPropagation();
    if (!confirm(`Reset test for ${userName}? This will clear their test answers and allow them to retake it.`)) {
      return;
    }

    try {
      const { ok } = await api.put(`/user/${userId}`, {
        training_test_answers: {},
        training_test_results: {},
        training_test_completed_at: null,
        training_test_evaluation_scores: {},
      });
      if (!ok) return toast.error("Failed to reset test");
      toast.success("Test reset! They can now retake it.");
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error("Error resetting test");
    }
  };

  const removeFromTraining = async (e, userId, userName) => {
    e.stopPropagation();
    if (!confirm(`Remove ${userName} from training? This will clear their training status.`)) {
      return;
    }

    try {
      const response = await api.put(`/user/${userId}`, {
        training_status: null,
      });

      if (!response.ok) {
        const errorMsg = response.error || response.data?.error || response.code || "Unknown error";
        return toast.error(`Failed to remove: ${errorMsg}`);
      }

      await loadData();
      toast.success(`${userName} removed from training`);
    } catch (error) {
      toast.error(`Error removing from training: ${error?.message || String(error)}`);
    }
  };

  const copyTestLink = (e, userId, userName) => {
    e.stopPropagation();
    const baseUrl = window.location.origin;
    const testLink = `${baseUrl}/selecole/training-test/${userId}`;
    navigator.clipboard.writeText(testLink);
    toast.success(`Test link copied for ${userName}!`);
  };

  if (loading) return <Loader />;

  // Group by status
  const usersByStatus = {
    in_training: users.filter((u) => u.training_status === "in_training"),
    ready_for_evaluation: users.filter((u) => u.training_status === "ready_for_evaluation"),
    graduated: users.filter((u) => u.training_status === "graduated"),
  };

  const filteredUsers = users.filter((u) => u.training_status === filterStatus);

  // Sort graduated users for table view
  const sortedGraduated = [...usersByStatus.graduated].sort((a, b) => {
    if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
    if (sortBy === "overall") return (b.training_evaluation_score || 0) - (a.training_evaluation_score || 0);
    if (sortBy === "prediction") return (b.training_performance_prediction || 0) - (a.training_performance_prediction || 0);
    return 0;
  });

  // Sort by start date
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (a.training_status === "in_training" && b.training_status === "in_training") {
      return new Date(b.training_start_date || 0) - new Date(a.training_start_date || 0);
    }
    return new Date(a.training_start_date || 0) - new Date(b.training_start_date || 0);
  });

  function getLevel(score) {
    if (!score) return 0;
    if (score >= 4.5) return 5;
    if (score >= 3.5) return 4;
    if (score >= 2.5) return 3;
    if (score >= 1.5) return 2;
    return 1;
  }

  function getGraduatesAtLevel(pillar, level) {
    return usersByStatus.graduated.filter((user) => {
      if (!user.training_evaluation_score) return false;
      let score;
      if (pillar === "skills") score = user.training_evaluation_skills;
      else if (pillar === "deliverables") score = user.training_evaluation_deliverables;
      else if (pillar === "culture") score = user.training_evaluation_culture;
      return getLevel(score) === level;
    });
  }

  function getCellColor(score) {
    if (!score) return "bg-gray-100";
    if (score >= 4.5) return "bg-purple-500/40";
    if (score >= 3.5) return "bg-teal-500/40";
    if (score >= 2.5) return "bg-blue-500/40";
    if (score >= 1.5) return "bg-yellow-500/40";
    return "bg-red-500/40";
  }

  const tabClass = (isActive) =>
    `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "" : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"}`;

  const activeColors = {
    in_training: "bg-blue-600 text-white",
    ready_for_evaluation: "bg-purple-600 text-white",
    graduated: "bg-green-600 text-white",
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Training Tracker</h1>
        </div>
        <div className="w-64">
          <SelectUser value={null} onChange={(user) => user && addToTraining(user)} placeholder="+ Add to training" />
        </div>
      </div>

      {/* Status Tabs — each is a real URL */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <NavLink
            to="/selecole/tracker/in-training"
            className={({ isActive }) => `${tabClass(isActive)} ${isActive ? activeColors.in_training : ""}`}>
            In Training ({usersByStatus.in_training.length})
          </NavLink>
          <NavLink
            to="/selecole/tracker/ready-for-evaluation"
            className={({ isActive }) => `${tabClass(isActive)} ${isActive ? activeColors.ready_for_evaluation : ""}`}>
            Ready for Evaluation ({usersByStatus.ready_for_evaluation.length})
          </NavLink>
          <NavLink
            to="/selecole/tracker/graduated"
            className={({ isActive }) => `${tabClass(isActive)} ${isActive ? activeColors.graduated : ""}`}>
            Graduated ({usersByStatus.graduated.length})
          </NavLink>
        </div>

        {/* View Toggle for Graduated */}
        {filterStatus === "graduated" && usersByStatus.graduated.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">View:</span>
            <button
              onClick={() => setViewMode("matrix")}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                viewMode === "matrix" ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
              }`}>
              Matrix
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                viewMode === "table" ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
              }`}>
              Table
            </button>
            {viewMode === "table" && (
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="ml-2 bg-white border border-gray-300 rounded-lg px-3 py-1 text-sm text-gray-700">
                <option value="name">Name</option>
                <option value="overall">Overall Score</option>
                <option value="prediction">Prediction</option>
              </select>
            )}
          </div>
        )}
      </div>

      {/* Graduated - Matrix View */}
      {filterStatus === "graduated" && usersByStatus.graduated.length > 0 && viewMode === "matrix" && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Training Performance Matrix</h3>

          <div className="overflow-x-auto">
            <div className="grid grid-cols-4 gap-1 mb-1 min-w-[800px]">
              <div></div>
              <div className="text-center text-sm font-medium text-gray-600 py-2">Deliverables (40%)</div>
              <div className="text-center text-sm font-medium text-gray-600 py-2">Skills (30%)</div>
              <div className="text-center text-sm font-medium text-gray-600 py-2">Culture (30%)</div>
            </div>

            {[
              { level: 5, label: "Exceptional", color: "bg-purple-100", textColor: "text-purple-700" },
              { level: 4, label: "Advanced", color: "bg-teal-100", textColor: "text-teal-700" },
              { level: 3, label: "Intermediate", color: "bg-blue-100", textColor: "text-blue-700" },
              { level: 2, label: "Basic", color: "bg-yellow-100", textColor: "text-yellow-700" },
              { level: 1, label: "Poor", color: "bg-red-100", textColor: "text-red-700" },
            ].map((levelData) => (
              <div key={levelData.level} className="grid grid-cols-4 gap-1 mb-1 min-w-[800px]">
                <div className={`flex items-center px-3 py-3 rounded-l-lg ${levelData.color}`}>
                  <span className={`text-xs font-medium ${levelData.textColor}`}>{levelData.label}</span>
                </div>
                {["deliverables", "skills", "culture"].map((pillar, idx) => (
                  <div
                    key={pillar}
                    className={`${levelData.color} p-2 min-h-[70px] flex flex-wrap gap-2 items-center justify-center ${idx === 2 ? "rounded-r-lg" : ""}`}>
                    {getGraduatesAtLevel(pillar, levelData.level).map((user) => (
                      <button
                        key={user._id + "-" + pillar}
                        onClick={() => navigate(`/selecole/tracker/evaluate/${user._id}`)}
                        className="w-10 h-10 rounded-full overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all shadow-sm"
                        title={`${user.name} - ${user["training_evaluation_" + pillar]?.toFixed(1)}`}>
                        <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-purple-600"></div>
                <span className="text-xs text-gray-600">Click avatar to view full evaluation</span>
              </div>
              <div className="text-xs text-gray-500">{usersByStatus.graduated.length} graduated trainees</div>
            </div>
          </div>
        </div>
      )}

      {/* Graduated - Table View */}
      {filterStatus === "graduated" && usersByStatus.graduated.length > 0 && viewMode === "table" && (
        <>
          <div className="flex items-center gap-6 text-sm mb-4">
            <span className="text-gray-600">Performance Level:</span>
            {[
              { color: "bg-purple-500/40", label: "Exceptional (4.5-5.0)" },
              { color: "bg-teal-500/40", label: "Advanced (3.5-4.4)" },
              { color: "bg-blue-500/40", label: "Intermediate (2.5-3.4)" },
              { color: "bg-yellow-500/40", label: "Basic (1.5-2.4)" },
              { color: "bg-red-500/40", label: "Poor (<1.5)" },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-1">
                <div className={`w-4 h-4 rounded ${l.color}`}></div>
                <span className="text-gray-700">{l.label}</span>
              </div>
            ))}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">Trainee</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Prediction</th>
                  <th className="text-center px-3 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider bg-amber-50">Deliverables (40%)</th>
                  <th className="text-center px-3 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider bg-blue-50">Skills (30%)</th>
                  <th className="text-center px-3 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider bg-teal-50">Culture (30%)</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Overall</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Test Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedGraduated.map((user) => {
                  const hasEvaluation = user.training_evaluation_score !== undefined;
                  return (
                    <tr key={user._id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/selecole/tracker/evaluate/${user._id}`)}>
                      <td className="px-4 py-3 sticky left-0 bg-white hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                          <span className="text-sm font-medium text-gray-900">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {user.training_performance_prediction ? (
                          <span
                            className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                              user.training_performance_prediction === 1
                                ? "bg-red-100 text-red-700"
                                : user.training_performance_prediction === 2
                                  ? "bg-yellow-100 text-yellow-700"
                                  : user.training_performance_prediction === 3
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-green-100 text-green-700"
                            }`}>
                            {user.training_performance_prediction === 1
                              ? "Below Bar"
                              : user.training_performance_prediction === 2
                                ? "Meets Bar"
                                : user.training_performance_prediction === 3
                                  ? "Above Bar"
                                  : "Overachiever"}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className={`px-3 py-3 text-center ${getCellColor(user.training_evaluation_deliverables)}`}>
                        {hasEvaluation ? <span className="text-sm font-semibold text-gray-900">{user.training_evaluation_deliverables?.toFixed(1)}</span> : <span className="text-xs text-gray-400">-</span>}
                      </td>
                      <td className={`px-3 py-3 text-center ${getCellColor(user.training_evaluation_skills)}`}>
                        {hasEvaluation ? <span className="text-sm font-semibold text-gray-900">{user.training_evaluation_skills?.toFixed(1)}</span> : <span className="text-xs text-gray-400">-</span>}
                      </td>
                      <td className={`px-3 py-3 text-center ${getCellColor(user.training_evaluation_culture)}`}>
                        {hasEvaluation ? <span className="text-sm font-semibold text-gray-900">{user.training_evaluation_culture?.toFixed(1)}</span> : <span className="text-xs text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {hasEvaluation ? (
                          <span
                            className={`inline-block px-3 py-1 rounded text-sm font-bold ${
                              user.training_evaluation_score >= 4.5
                                ? "bg-purple-100 text-purple-700"
                                : user.training_evaluation_score >= 3.5
                                  ? "bg-teal-100 text-teal-700"
                                  : user.training_evaluation_score >= 2.5
                                    ? "bg-blue-100 text-blue-700"
                                    : user.training_evaluation_score >= 1.5
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-red-100 text-red-700"
                            }`}>
                            {user.training_evaluation_score?.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {user.training_test_results ? (
                          <span className="text-sm text-gray-700">
                            {user.training_test_results.correct}/{user.training_test_results.totalQuestions}
                            <span className="text-xs text-gray-500 ml-1">({Math.round((user.training_test_results.correct / user.training_test_results.totalQuestions) * 100)}%)</span>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">No test</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {filterStatus === "graduated" && usersByStatus.graduated.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-400">No graduated trainees yet</div>
      )}

      {/* Users List (for in_training and ready_for_evaluation) */}
      {filterStatus !== "graduated" && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {sortedUsers.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">No trainees in this category</div>
          ) : (
            <div>
              {sortedUsers.map((user) => {
                const statusInfo = STATUS_LABELS[user.training_status] || STATUS_LABELS.in_training;
                const daysIn = getDaysInTraining(user.training_start_date);
                const hasEvaluation = user.training_evaluation_score !== undefined;

                return (
                  <div
                    key={user._id}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-0"
                    onClick={() => navigate(`/selecole/tracker/evaluate/${user._id}`)}>
                    <div className="flex items-center gap-3 flex-1">
                      <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{user.name}</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${statusInfo.color}`}>
                            {statusInfo.emoji} {statusInfo.label}
                          </span>
                          {hasEvaluation && <span className="text-xs text-gray-500">Score: {user.training_evaluation_score?.toFixed(1)}/5.0</span>}
                          {user.training_performance_prediction && (
                            <span
                              className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                user.training_performance_prediction === 1
                                  ? "bg-red-100 text-red-700"
                                  : user.training_performance_prediction === 2
                                    ? "bg-yellow-100 text-yellow-700"
                                    : user.training_performance_prediction === 3
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-green-100 text-green-700"
                              }`}>
                              {user.training_performance_prediction === 1
                                ? "⚠️ Below Bar"
                                : user.training_performance_prediction === 2
                                  ? "✓ Meets Bar"
                                  : user.training_performance_prediction === 3
                                    ? "⭐ Above Bar"
                                    : "🚀 Overachiever"}
                            </span>
                          )}
                          {user.training_test_completed_at && (
                            <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded">
                              Test: {user.training_test_results?.correct}/{user.training_test_results?.totalQuestions}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          {user.position && <span className="text-xs text-gray-500">{user.position}</span>}
                          {user.training_start_date && <span className="text-xs text-gray-400">Day {daysIn + 1} of training</span>}
                          {user.training_graduated_at && <span className="text-xs text-gray-400">Graduated {new Date(user.training_graduated_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {user.training_status === "in_training" && (
                        <>
                          {!user.training_test_completed_at && (
                            <>
                              <button
                                onClick={(e) => copyTestLink(e, user._id, user.name)}
                                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-1"
                                title="Copy test link to send to trainee">
                                <FaLink /> Copy Test Link
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/selecole/tracker/test/${user._id}`);
                                }}
                                className="px-3 py-1 text-xs bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center gap-1"
                                title="Take training test">
                                📝 Test
                              </button>
                            </>
                          )}
                          {user.training_test_completed_at && (
                            <button
                              onClick={(e) => resetTest(e, user._id, user.name)}
                              className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors flex items-center gap-1"
                              title="Reset test to allow retake">
                              🔄 Reset Test
                            </button>
                          )}
                          <button
                            onClick={(e) => markReadyForEvaluation(e, user._id)}
                            className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center gap-1"
                            title="Mark as ready for evaluation">
                            <FaClipboardCheck /> Ready
                          </button>
                        </>
                      )}
                      {user.training_status === "ready_for_evaluation" && (
                        <>
                          {user.training_test_completed_at && (
                            <button
                              onClick={(e) => resetTest(e, user._id, user.name)}
                              className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors flex items-center gap-1"
                              title="Reset test to allow retake">
                              🔄 Reset Test
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/selecole/tracker/evaluate/${user._id}`);
                            }}
                            className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-1"
                            title="Evaluate trainee">
                            <FaUserGraduate /> Evaluate
                          </button>
                        </>
                      )}
                      <button
                        onClick={(e) => removeFromTraining(e, user._id, user.name)}
                        className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        title="Remove from training">
                        <FaTrash />
                      </button>
                      <FaChevronRight className="text-gray-300 w-3 h-3" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
